import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiClipboard, FiBarChart2, FiBookOpen, FiClock,
    FiCreditCard, FiFileText, FiCalendar, FiUser,
    FiAlertTriangle, FiArrowRight,
} from "react-icons/fi";
import { useApp } from "../../../context/AppContext";
import portalService from "../../../services/portalService";
import CollegeLogoBadge from "../../../components/common/CollegeLogoBadge";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;
const extract = (r) => r?.data ?? r;

const GRADE_COLORS = {
    O: "#15803d", "A+": "#002747", A: "#003b67",
    "B+": "#92400e", B: "#d97706", F: "#dc2626", Ab: "#6b7280",
};

export default function StudentDashboard() {
    const { user, collegeName } = useApp();
    const navigate = useNavigate();

    const [profile, setProfile] = useState(null);
    const [attendance, setAttendance] = useState(null);
    const [results, setResults] = useState([]);
    const [exams, setExams] = useState([]);
    const [fees, setFees] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            portalService.getProfile(),
            portalService.getAttendance(),
            portalService.getResults(),
            portalService.getExams(),
            portalService.getFees(),
            portalService.getEvents(),
        ])
            .then(([p, a, r, e, f, ev]) => {
                setProfile(extract(p));
                setAttendance(extract(a));
                setResults(extract(r) || []);
                setExams(extract(e) || []);
                setFees(extract(f));
                setEvents(extract(ev) || []);
            })
            .catch((err) => toast.error(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="loading-center"><div className="spinner" />Loading your dashboard…</div>;

    const attPct = attendance?.summary?.percentage ?? 0;
    const attColor = attPct >= 75 ? "#16a34a" : attPct >= 60 ? "#d97706" : "#dc2626";
    const latestResult = Array.isArray(results) ? results[0] : null;
    const todayStr = new Date().toISOString().slice(0, 10);
    const todayEvents = Array.isArray(events) ? events.filter((e) => e.date === todayStr) : [];

    return (
        <div>
            {/* Header */}
            <div className="page-header" style={{ marginBottom: "1.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                    <CollegeLogoBadge size={52} />
                    <div>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#003b67", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                            {collegeName}
                        </div>
                        <h1 className="page-title" style={{ margin: "2px 0 0 0" }}>
                            Welcome, {profile?.fullName?.split(" ")?.[0] || user?.displayName} 👋
                        </h1>
                        <p className="page-subtitle" style={{ margin: "2px 0 0 0" }}>
                            {profile?.course?.shortName || "Course"} {profile?.yearNumber ? `· Year ${profile?.yearNumber}` : ""} · {profile?.academicYear?.label}
                        </p>
                    </div>
                </div>
            </div>

            {/* Low attendance warning */}
            {attPct > 0 && attPct < 75 && (
                <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1.25rem", fontSize: 13, color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
                    <FiAlertTriangle size={14} />
                    Your attendance is <strong>{attPct}%</strong> — below the required 75%.
                </div>
            )}

            {/* Today events banner */}
            {todayEvents.length > 0 && (
                <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1.25rem", fontSize: 13, color: "#002747", display: "flex", alignItems: "center", gap: 8 }}>
                    <FiCalendar size={14} />
                    Today: <strong>{todayEvents.map((e) => e.title).join(" · ")}</strong>
                </div>
            )}

            {/* Quick stat cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "0.85rem", marginBottom: "1.25rem" }}>
                {[
                    { icon: FiClipboard, label: "Attendance", to: "/student/attendance", color: attColor, value: `${attPct}%`, sub: `${attendance?.summary?.present ?? 0} present days` },
                    { icon: FiBarChart2, label: "Results", to: "/student/results", color: "#7c3aed", value: Array.isArray(results) ? results.length : 0, sub: "exams recorded" },
                    { icon: FiCreditCard, label: "Fees Pending", to: "/student/fees", color: "#dc2626", value: inr(fees?.pending ?? 0), sub: "outstanding" },
                    { icon: FiBookOpen, label: "Upcoming Exams", to: "/student/exams", color: "#003b67", value: Array.isArray(exams) ? exams.length : 0, sub: "scheduled" },
                ].map(({ icon: Icon, label, to, color, value, sub }) => (
                    <div key={to} className="card"
                        style={{ padding: "1rem", borderLeft: `4px solid ${color}`, cursor: "pointer", transition: "box-shadow 0.15s" }}
                        onClick={() => navigate(to)}
                        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.1)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "var(--shadow)"; }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
                            <Icon size={14} color={color} />
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>{label}</div>
                        {sub && <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>{sub}</div>}
                    </div>
                ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: "1rem", alignItems: "start" }}>

                {/* Attendance summary */}
                <div style={{ display: "grid", gap: "1rem" }}>
                    <div className="card">
                        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}><FiClipboard size={13} /> Attendance This Month</span>
                            <button className="btn btn-outline btn-sm" onClick={() => navigate("/student/attendance")}
                                style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>View All <FiArrowRight size={12} /></button>
                        </div>
                        <div className="card-body">
                            <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", flexWrap: "wrap" }}>
                                <svg width={80} height={80}>
                                    <circle cx={40} cy={40} r={32} fill="none" stroke={`${attColor}20`} strokeWidth={8} />
                                    <circle cx={40} cy={40} r={32} fill="none" stroke={attColor} strokeWidth={8}
                                        strokeDasharray={`${2 * Math.PI * 32}`}
                                        strokeDashoffset={`${2 * Math.PI * 32 * (1 - attPct / 100)}`}
                                        strokeLinecap="round" transform="rotate(-90 40 40)" />
                                    <text x={40} y={38} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 14, fontWeight: 800, fill: attColor }}>{attPct}%</text>
                                    <text x={40} y={52} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 9, fill: "#94a3b8" }}>present</text>
                                </svg>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem 1.5rem" }}>
                                    {[
                                        ["Present", attendance?.summary?.present ?? 0, "#16a34a"],
                                        ["Absent", attendance?.summary?.absent ?? 0, "#dc2626"],
                                        ["Late", attendance?.summary?.late ?? 0, "#d97706"],
                                        ["Leave", attendance?.summary?.leave ?? 0, "#003b67"],
                                    ].map(([l, v, c]) => (
                                        <div key={l}>
                                            <div style={{ fontSize: 18, fontWeight: 700, color: c }}>{v}</div>
                                            <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{l}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Latest result */}
                    {latestResult && (
                        <div className="card">
                            <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}><FiBarChart2 size={13} /> Latest Result</span>
                                <button className="btn btn-outline btn-sm" onClick={() => navigate("/student/results")}>View All</button>
                            </div>
                            <div className="card-body">
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: 14 }}>{latestResult.exam?.title}</div>
                                        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>{latestResult.exam?.examType}</div>
                                        <div style={{ fontSize: 13, marginTop: 6 }}>
                                            <strong>{latestResult.totalObtained}/{latestResult.totalMaxMarks}</strong>
                                            <span style={{ marginLeft: 8, color: "var(--text-muted)" }}>{latestResult.percentage}%</span>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: 28, fontWeight: 900, color: GRADE_COLORS[latestResult.grade] || "#6b7280", lineHeight: 1 }}>{latestResult.grade}</div>
                                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{latestResult.result}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right sidebar */}
                <div style={{ display: "grid", gap: "1rem" }}>
                    {/* Quick links */}
                    <div className="card">
                        <div className="card-header" style={{ fontSize: 13 }}>Quick Links</div>
                        <div style={{ padding: "0.5rem" }}>
                            {[
                                { icon: FiUser, label: "My Profile", to: "/student/profile" },
                                { icon: FiClock, label: "Timetable", to: "/student/timetable" },
                                { icon: FiFileText, label: "Certificates", to: "/student/certificates" },
                                { icon: FiCalendar, label: "Events", to: "/student/events" },
                            ].map(({ icon: Icon, label, to }) => (
                                <button key={to} onClick={() => navigate(to)}
                                    style={{ width: "100%", display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.55rem 0.75rem", background: "none", border: "none", cursor: "pointer", fontSize: 13, borderRadius: 6, textAlign: "left", transition: "background 0.1s" }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = "var(--primary-light)"; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}>
                                    <Icon size={14} color="var(--primary)" />{label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Upcoming events */}
                    {Array.isArray(events) && events.length > 0 && (
                        <div className="card">
                            <div className="card-header" style={{ fontSize: 13, display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                <FiCalendar size={13} /> Upcoming Events
                            </div>
                            <div>
                                {events.slice(0, 4).map((ev) => (
                                    <div key={ev._id} style={{ padding: "0.55rem 1rem", borderBottom: "1px solid var(--border)", fontSize: 13 }}>
                                        <div style={{ fontWeight: 600 }}>{ev.title}</div>
                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                            {ev.date}
                                            {ev.isHoliday && <span style={{ marginLeft: 6, fontSize: 10, background: "#fee2e2", color: "#dc2626", padding: "1px 5px", borderRadius: 99, fontWeight: 700 }}>Holiday</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}